import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  Upload, Loader2, Check, AlertCircle, Plus, Trash2, FileText,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ResizablePanelGroup, ResizablePanel, ResizableHandle,
} from "@/components/ui/resizable";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import {
  extractCustomerPo, type ExtractCustomerPoResponse, type AccountMatch,
} from "@/lib/dashboard-api";
import { uploadDocumentToStorage } from "./upload-helpers";
import { downloadAttachmentUrl } from "@/lib/files-api";

type Step = "upload" | "extracting" | "review";

interface EditableLine {
  id: string;
  description: string;
  quantity: string;
  uom: string;
  unitPrice: string;
  lineTotal: string;
  customerLineNumber: string;
  needsReview: boolean;
}

interface UploadCustomerPoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UploadCustomerPoModal({ open, onOpenChange }: UploadCustomerPoModalProps) {
  const { sessionId } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [, setLocation] = useLocation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [step, setStep] = useState<Step>("upload");
  const [fileName, setFileName] = useState<string>("");
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [extraction, setExtraction] = useState<ExtractCustomerPoResponse | null>(null);

  // Editable form state
  const [accountId, setAccountId] = useState<string | null>(null);
  const [accountName, setAccountName] = useState<string>("");
  const [customerPoNumber, setCustomerPoNumber] = useState("");
  const [poDate, setPoDate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [shipTo, setShipTo] = useState("");
  const [instructions, setInstructions] = useState("");
  const [lines, setLines] = useState<EditableLine[]>([]);

  // Reset everything when modal opens
  useEffect(() => {
    if (open) return;
    setStep("upload");
    setFileName(""); setFilePreviewUrl(null); setExtraction(null);
    setAccountId(null); setAccountName(""); setCustomerPoNumber("");
    setPoDate(""); setPaymentTerms(""); setShipTo(""); setInstructions("");
    setLines([]);
  }, [open]);

  // ---- Mutations ----------------------------------------------------------
  const extractMut = useMutation({
    mutationFn: async (file: File) => {
      const slot = await uploadDocumentToStorage(file, "customer_po", sessionId);
      const result = await extractCustomerPo({
        storageKey: slot.storageKey,
        originalFilename: slot.filename,
      }, sessionId);
      return { result, file };
    },
    onSuccess: ({ result, file }) => {
      setExtraction(result);
      setFilePreviewUrl(URL.createObjectURL(file));
      setAccountId(result.customerPo.accountId);
      setAccountName(result.accountMatch?.accountName ?? "");
      setCustomerPoNumber(result.customerPo.customerPoNumber ?? "");
      setPoDate(result.customerPo.customerPoDate ?? "");
      setPaymentTerms(result.customerPo.customerPaymentTerms ?? "");
      setShipTo(result.customerPo.originalParsedShipToRaw ?? "");
      setInstructions(result.customerPo.customerInstructions ?? "");
      setLines(result.customerPo.lines.map((l) => ({
        id: l.id,
        description: l.customerDescription ?? "",
        quantity: l.quantity || "",
        uom: l.customerUom ?? "each",
        unitPrice: l.unitPrice ?? "",
        lineTotal: l.extendedAmount ?? "",
        customerLineNumber: l.customerLineNumber,
        needsReview: l.originalParsedQuantity === null,
      })));
      setStep("review");
    },
    onError: (e: Error) => {
      toast({ title: "Extraction failed", description: e.message, variant: "destructive" });
      setStep("upload");
    },
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!extraction) throw new Error("No extraction loaded");
      const cpoId = extraction.customerPoId;
      // Patch header
      await api.patch(`/customer-pos/${cpoId}`, {
        accountId: accountId,
        customerPoNumber: customerPoNumber || null,
        customerPoDate: poDate || null,
        customerPaymentTerms: paymentTerms || null,
        customerInstructions: instructions || null,
        status: "ACKNOWLEDGED",
      }, sessionId);
      // Patch lines (line state preserved when AI got it right; we just sync edits)
      for (const ln of lines) {
        await api.patch(`/customer-pos/${cpoId}/lines/${ln.id}`, {
          customerDescription: ln.description,
          quantity: ln.quantity || "0",
          customerUom: ln.uom,
          unitPrice: ln.unitPrice || null,
          extendedAmount: ln.lineTotal || null,
        }, sessionId);
      }
      return cpoId;
    },
    onSuccess: (cpoId) => {
      toast({ title: "Customer PO saved", description: "Opening for review…" });
      qc.invalidateQueries({ queryKey: ["/api/customer-pos"] });
      onOpenChange(false);
      setLocation(`/customer-pos/${cpoId}`);
    },
    onError: (e: Error) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  // ---- Handlers -----------------------------------------------------------
  function handleFile(f: File) {
    setFileName(f.name);
    setStep("extracting");
    extractMut.mutate(f);
  }

  function updateLine(id: string, patch: Partial<EditableLine>) {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }

  const accountMatch: AccountMatch | null = extraction?.accountMatch ?? null;
  const matchHighConfidence = (accountMatch?.confidence ?? 0) >= 0.5;
  const paymentTermsMatchAccount =
    accountMatch?.paymentTerms && paymentTerms &&
    accountMatch.paymentTerms.toLowerCase().replace(/\s+/g, "") ===
      paymentTerms.toLowerCase().replace(/\s+/g, "");

  const poTotal = lines.reduce((sum, l) => sum + (parseFloat(l.lineTotal) || 0), 0);
  const canSave =
    !!accountId &&
    !!customerPoNumber.trim() &&
    lines.length > 0 &&
    lines.every((l) => Number(l.quantity) > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "p-0",
          step === "review"
            ? "max-w-[95vw] w-[95vw] h-[90vh]"
            : "max-w-xl",
        )}
      >
        {step !== "review" && (
          <DialogHeader className="p-6 pb-4 border-b">
            <DialogTitle>
              {step === "upload" && "Upload Customer PO"}
              {step === "extracting" && "Extracting PO data…"}
            </DialogTitle>
          </DialogHeader>
        )}

        {step === "upload" && (
          <div className="p-6 space-y-4">
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) handleFile(f);
              }}
              className="border-2 border-dashed rounded-lg py-10 text-center cursor-pointer hover:bg-muted/30 transition-colors"
            >
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm font-medium">Drop a PO here, or click to browse</p>
              <p className="text-xs text-muted-foreground mt-1">PDF, PNG, or JPG · up to 90 MB</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,image/*"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {step === "extracting" && (
          <div className="p-10 flex flex-col items-center justify-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
            <p className="text-sm font-medium">Reading {fileName}</p>
            <p className="text-xs text-muted-foreground">
              Power AI is identifying the customer, line items, and payment terms…
            </p>
          </div>
        )}

        {step === "review" && extraction && (
          <ResizablePanelGroup direction="horizontal" className="h-full">
            <ResizablePanel defaultSize={50} minSize={30}>
              <div className="h-full overflow-y-auto p-6 space-y-4">
                <DialogHeader>
                  <DialogTitle>Review Customer PO</DialogTitle>
                </DialogHeader>

                {/* AI match banner */}
                {accountMatch && matchHighConfidence ? (
                  <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
                    <Check className="h-4 w-4 shrink-0" />
                    <span>
                      OpenSupply AI Match: <span className="font-bold">{accountMatch.accountName}</span>
                    </span>
                  </div>
                ) : (
                  <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>No confident customer match — pick one below.</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Customer</Label>
                    <div className="flex gap-2">
                      <Input
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        placeholder="Customer name"
                      />
                      <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" disabled>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Customer PO #</Label>
                    <Input value={customerPoNumber} onChange={(e) => setCustomerPoNumber(e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">PO Date</Label>
                    <Input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Payment Terms</Label>
                    <Input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} />
                    {paymentTermsMatchAccount && (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 flex items-center gap-1">
                        <Check className="h-3 w-3" /> Matches ({accountMatch?.paymentTerms})
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Ship-To Address</Label>
                    <Textarea
                      rows={3}
                      value={shipTo}
                      onChange={(e) => setShipTo(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Notes / Special Instructions</Label>
                    <Textarea
                      rows={3}
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                    />
                  </div>
                </div>

                {/* Lines */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Line Items</Label>
                    <Button variant="outline" size="sm" onClick={() => setLines((prev) => [
                      ...prev,
                      {
                        id: `new-${prev.length}`, description: "", quantity: "1", uom: "each",
                        unitPrice: "", lineTotal: "", customerLineNumber: String(prev.length + 1),
                        needsReview: false,
                      },
                    ])}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add Line
                    </Button>
                  </div>
                  <div className="rounded-md border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/30 text-xs">
                        <tr>
                          <th className="px-2 py-1.5 text-left">#</th>
                          <th className="px-2 py-1.5 text-left">Description</th>
                          <th className="px-2 py-1.5 text-right">Qty</th>
                          <th className="px-2 py-1.5 text-left">UOM</th>
                          <th className="px-2 py-1.5 text-right">Unit Price</th>
                          <th className="px-2 py-1.5 text-right">Line Total</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((ln, idx) => (
                          <tr key={ln.id} className={cn("border-t", ln.needsReview && "bg-amber-500/5")}>
                            <td className="px-2 py-1 text-xs">{idx + 1}</td>
                            <td className="px-2 py-1">
                              <Input
                                value={ln.description}
                                onChange={(e) => updateLine(ln.id, { description: e.target.value })}
                                className="h-8"
                              />
                            </td>
                            <td className="px-2 py-1 w-24">
                              <Input
                                type="number"
                                value={ln.quantity}
                                onChange={(e) => updateLine(ln.id, { quantity: e.target.value, needsReview: false })}
                                className={cn("h-8 text-right", ln.needsReview && "border-amber-500")}
                              />
                            </td>
                            <td className="px-2 py-1 w-20">
                              <Input
                                value={ln.uom}
                                onChange={(e) => updateLine(ln.id, { uom: e.target.value })}
                                className="h-8"
                              />
                            </td>
                            <td className="px-2 py-1 w-24">
                              <Input
                                type="number"
                                step="0.01"
                                value={ln.unitPrice}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  updateLine(ln.id, {
                                    unitPrice: v,
                                    lineTotal: v && ln.quantity
                                      ? (Number(v) * Number(ln.quantity)).toFixed(2)
                                      : ln.lineTotal,
                                  });
                                }}
                                className="h-8 text-right"
                              />
                            </td>
                            <td className="px-2 py-1 w-28 text-right text-sm">
                              {ln.lineTotal ? `$${Number(ln.lineTotal).toFixed(2)}` : "—"}
                            </td>
                            <td className="px-2 py-1 w-8">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive"
                                onClick={() => setLines((prev) => prev.filter((p) => p.id !== ln.id))}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t">
                          <td colSpan={5} className="px-2 py-1.5 text-right text-sm font-medium">PO Total:</td>
                          <td className="px-2 py-1.5 text-right text-sm font-semibold">${poTotal.toFixed(2)}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                  <Button
                    onClick={() => saveMut.mutate()}
                    disabled={!canSave || saveMut.isPending}
                  >
                    {saveMut.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                    Save & Review
                  </Button>
                </div>
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel defaultSize={50} minSize={20}>
              <div className="h-full flex flex-col">
                <div className="px-3 py-2 border-b flex items-center gap-2 text-xs">
                  <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate">{fileName}</span>
                </div>
                {filePreviewUrl ? (
                  <iframe
                    src={filePreviewUrl}
                    className="flex-1 w-full border-0 bg-muted"
                    title="PO preview"
                  />
                ) : extraction?.attachmentId ? (
                  <iframe
                    src={downloadAttachmentUrl(extraction.attachmentId, sessionId)}
                    className="flex-1 w-full border-0 bg-muted"
                    title="PO preview"
                  />
                ) : (
                  <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground">
                    Preview unavailable
                  </div>
                )}
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        )}
      </DialogContent>
    </Dialog>
  );
}
